import React, { useEffect, useState } from 'react';
import { Modal, Input, Table, Button, Tag, Typography, message } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { AdminService } from '../Services/AdminService';
import { WebApiAdminDtoAdminMemberSearchResultDto, WebApiModelsEnumerationTypesMemberStatus, WebApiModelsEnumerationTypesMemberType } from '../Repository/eventbookyapi';
import { ResponseError } from '../Repository/eventbookyapi/runtime';
import { getCurrentLanguage } from '../Helpers/RouteHelper';

interface Props {
    visible: boolean;
    companyId: number | null;
    companyName: string;
    onCancel: () => void;
}

const memberStatusLabels: Record<number, string> = {
    [WebApiModelsEnumerationTypesMemberStatus.NUMBER_1]: 'Cancelled',
    [WebApiModelsEnumerationTypesMemberStatus.NUMBER_2]: 'Unconfirmed email',
    [WebApiModelsEnumerationTypesMemberStatus.NUMBER_3]: 'Unconfirmed initial payment',
};

const getErrorMessage = async (error: unknown, fallback: string) => {
    if (error instanceof ResponseError) {
        const body = await error.response.json().catch(() => null);
        return body?.message || fallback;
    }
    return fallback;
};

const AdminLoginAsMemberModal: React.FC<Props> = ({ visible, companyId, companyName, onCancel }) => {
    const [members, setMembers] = useState<WebApiAdminDtoAdminMemberSearchResultDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');

    const search = async (q: string) => {
        if (!companyId) return;
        setLoading(true);
        try {
            setMembers(await AdminService.SearchMembers(companyId, q));
        } catch (e) {
            message.error(await getErrorMessage(e, 'Failed to search members'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible) {
            setQuery('');
            search('');
        } else {
            setMembers([]);
        }
    }, [visible, companyId]);

    const onLoginClick = async (member: WebApiAdminDtoAdminMemberSearchResultDto) => {
        if (!companyId) return;
        // Open the tab synchronously so the browser doesn't treat it as a popup after the await
        const tab = window.open('', '_blank');
        try {
            const { siteUrl } = await AdminService.LoginAsMember(companyId, member.memberId!);
            const url = `${process.env.REACT_APP_WEB_URL}/${getCurrentLanguage()}/${siteUrl}/editmember`;
            if (tab) {
                tab.location.href = url;
            } else {
                window.open(url, '_blank');
            }
        } catch (e) {
            tab?.close();
            message.error(await getErrorMessage(e, 'Failed to log in as member'));
        }
    };

    const columns: ColumnsType<WebApiAdminDtoAdminMemberSearchResultDto> = [
        {
            title: 'Name',
            key: 'name',
            render: (_, m) => (
                <>
                    {m.firstName} {m.lastName}
                    {m.memberType === WebApiModelsEnumerationTypesMemberType.NUMBER_1 && <Tag style={{ marginLeft: 8 }}>Family</Tag>}
                    {m.status != null && memberStatusLabels[m.status] && <Tag color="orange" style={{ marginLeft: 8 }}>{memberStatusLabels[m.status]}</Tag>}
                </>
            ),
        },
        { title: 'Email', dataIndex: 'email', key: 'email' },
        { title: 'Unique id', dataIndex: 'uniqueId', key: 'uniqueId' },
        { title: 'Id', dataIndex: 'memberId', key: 'memberId' },
        {
            title: '',
            key: 'action',
            render: (_, m) => <Button type="primary" onClick={() => onLoginClick(m)}>Log in</Button>,
        },
    ];

    return (
        <Modal
            open={visible}
            title={`Log in as member – ${companyName}`}
            onCancel={onCancel}
            footer={null}
            width={900}
            destroyOnClose
        >
            <Input.Search
                placeholder="Search by name, email, member id or unique id..."
                allowClear
                value={query}
                onChange={e => setQuery(e.target.value)}
                onSearch={search}
                enterButton
                style={{ marginBottom: 16 }}
            />
            <Table
                columns={columns}
                dataSource={members}
                rowKey="memberId"
                loading={loading}
                size="small"
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
            />
            {members.length === 50 && (
                <Typography.Text type="secondary">Showing the first 50 matches – refine the search to find others.</Typography.Text>
            )}
        </Modal>
    );
};

export default AdminLoginAsMemberModal;
